import { z } from "zod";
import { CategoryKind, Transaction, TxnStatus } from "../domain.js";
import {
  BooleanQuery,
  Direction,
  Id,
  IdList,
  IsoDate,
  IsoMonth,
  page,
  Pence,
} from "../primitives.js";
import { RangePeriod } from "./query.js";

/**
 * The ledger's read and write shapes.
 *
 * Every filter here is also a URL parameter on `/transactions`, because a
 * filtered list that cannot be sent to the other person in the household is
 * half a feature.
 */

/**
 * Which column the list is in order of.
 *
 * The five the header row offers, and no more: a ledger is read by when, where,
 * what, what for and how much, and a sort on `updated_at` is a database's idea
 * of a useful question rather than a person's.
 */
export const TransactionSort = z.enum(["date", "account", "description", "category", "amount"]);

export const SortDirection = z.enum(["asc", "desc"]);

/**
 * Which way a column reads when nobody has said: newest first for the date and
 * biggest first for the money, because that is what anyone opening a ledger
 * wants at the top, and A–Z for the three columns of words.
 *
 * Shared rather than decided in the SQL, so `?sort=amount` with no direction on
 * it cannot mean one thing to the list and another to the arrow drawn above it.
 */
export function defaultSortDirection(sort: TransactionSort): SortDirection {
  return sort === "date" || sort === "amount" ? "desc" : "asc";
}

export const TransactionFilter = z.object({
  month: IsoMonth.optional(),
  /**
   * One of the picker's ranges, resolved server-side against the same clock
   * the DSL uses. Takes precedence over `month` when both are somehow sent —
   * the picker itself never sends both at once.
   */
  period: RangePeriod.optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  accountId: Id.optional(),
  /**
   * Several accounts at once, for a list that has to match a figure drawn from
   * several — the dashboard's breakdown, once its own account focus is on. The
   * transactions screen filters one account at a time and sends `accountId`;
   * both narrow the same column, so sending both is an intersection.
   */
  accountIds: IdList.optional(),
  categoryId: Id.optional(),
  personId: Id.optional(),
  /** Rows one statement upload added — the drill-through from Import. */
  statementId: Id.optional(),
  direction: Direction.optional(),
  /** Free text over the description. */
  q: z.string().trim().min(1).max(200).optional(),
  /** Magnitudes, as in the DSL: `minPence=5000` is "£50 or more, either way". */
  minPence: z.coerce.number().int().min(0).optional(),
  maxPence: z.coerce.number().int().min(0).optional(),
  status: TxnStatus.optional(),
  /**
   * Show rows somebody hid by hand, which are out of the list and out of the
   * totals otherwise.
   *
   * Transfers are **not** affected by this and never were. They stay in the list
   * because they happened, and are dropped from the money figures by their
   * category's kind instead.
   */
  excluded: BooleanQuery.optional(),
  uncategorised: BooleanQuery.optional(),
  /**
   * The review queue: transfer rows that no total counts and nobody has
   * classified — no counterpart in the ledger, no `kindOverride`.
   *
   * Every one of these is money that crossed the edge of what the ledger tracks,
   * so it is real income or real spending being left out. Some accounts will
   * never be imported, so this is a state to be answered rather than a queue
   * that empties itself.
   */
  unclassifiedTransfer: BooleanQuery.optional(),
  /**
   * Ask for `budgets` alongside the page. Opt-in because it is a second pass
   * over every row of the months the page spans, and most callers — the review
   * queue, the drill-through from /ask — have no budget to draw against.
   */
  withBudget: BooleanQuery.optional(),
  sort: TransactionSort.default("date"),
  /** Absent is `defaultSortDirection(sort)`, resolved in one place. */
  dir: SortDirection.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  /**
   * Opaque, and roomy because of what is inside it: the cursor carries the
   * value of whatever column the list is sorted by, which for a description
   * sort is a whole description. Truncating that would page over rows twice.
   */
  cursor: z.string().max(4096).optional(),
});

/**
 * Where a category stood against its budget at the moment of one transaction.
 *
 * `spentPence` is the month's spending in that category up to **and
 * including** this row, so the figure answers "where was I when this went
 * out" rather than "where did the month end up" — which is what makes it
 * possible to see which payment took a category over.
 *
 * It is counted exactly as the budgets screen counts spending: booked, not
 * excluded, not a transfer, and only money going out. Two screens that
 * disagree about what has gone on Travel this month are worse than one screen.
 *
 * An entry with a **null** `allowancePence` is a category worth budgeting that
 * nobody has budgeted — a real answer, and the one the ledger turns into an
 * offer to set one. It is not the same as no entry at all, which is what a row
 * with nothing to compare gets: an unfiled row, a transfer between the
 * household's own accounts, or income.
 */
export const BudgetProgress = z.object({
  transactionId: Id,
  categoryId: Id,
  /** Positive magnitude, as spend is shown everywhere else. */
  spentPence: Pence,
  /** What that category was allowed that month; null when it has no budget. */
  allowancePence: Pence.nullable(),
});

export const TransactionList = page(Transaction).extend({
  /**
   * The totals for the *whole* filtered set, not the page. A list that shows
   * fifty rows and the sum of fifty rows is a list that lies about the month.
   *
   * `count` counts every row the list holds. The money figures count fewer:
   * transfers between the household's own accounts are listed, because they
   * happened, but summing them would invent income and spending that did not.
   * `transferCount` is the difference, so the screen can say it out loud.
   *
   * In, out and saving are decided by the kind of the category a row is filed
   * under rather than by the sign of its amount — the same split the dashboard
   * uses, so the two screens cannot mean different things by "out".
   */
  totals: z.object({
    inPence: Pence,
    outPence: Pence,
    savingPence: Pence,
    count: z.number().int(),
    transferCount: z.number().int(),
  }),
  /**
   * Present only when `withBudget` was asked for, and then only for the rows
   * of this page that have something to compare — so it is shorter than
   * `data` rather than padded with nulls.
   */
  budgets: z.array(BudgetProgress).optional(),
});

/** Every month the household has activity in, newest first. */
export const MonthList = z.object({ data: z.array(IsoMonth) });

/**
 * The fields an edit can set on many rows at once. `categoryId` also rewrites
 * the denormalised category names, and stamps `category_source = 'manual'` —
 * which is what makes a later rule re-run leave it alone.
 */
const SharedFields = z.object({
  categoryId: Id.nullable(),
  /**
   * What these rows count as, overriding their categories' kind. Null clears it
   * and hands the decision back to the category.
   *
   * Bulk-editable on purpose: "these eleven payments from Sam are all income" is
   * one decision about eleven rows, and it is the decision that turns a review
   * queue into a few clicks rather than a chore.
   */
  kindOverride: CategoryKind.nullable(),
  personId: Id.nullable(),
  notes: z.string().max(2000).nullable(),
  excluded: z.boolean(),
});

const nonEmpty = (patch: object) => Object.keys(patch).length > 0;

/**
 * A partial update of one row.
 *
 * `description` is here and not in the bulk shape on purpose: a description is
 * what tells two rows of the same date and amount apart, so setting forty of
 * them to one string only destroys the thing that distinguishes them.
 */
export const TransactionPatch = SharedFields.extend({
  description: z.string().trim().min(1).max(500),
})
  .partial()
  .refine(nonEmpty, "Nothing to change.");

/**
 * Bulk edit is atomic and audited as one entry, because "I categorised 40
 * things and then undid it" must be one undo.
 */
export const TransactionBulkPatch = z.object({
  ids: z.array(Id).min(1).max(500),
  patch: SharedFields.partial().refine(nonEmpty, "Nothing to change."),
});

/**
 * Matching the two halves of a transfer. A month, or the whole ledger when the
 * month is left off — which is what a first run wants.
 */
export const TransferMatchQuery = z.object({ month: IsoMonth.optional() });

export const TransferMatchResult = z.object({
  /** Transfer legs considered: unclassified, not excluded, in the span. */
  scanned: z.number().int(),
  /** Pairs found. Each accounts for two of `scanned`. */
  paired: z.number().int(),
  /**
   * Legs with no other half in the ledger. **Not a failure count.** Each is money
   * that crossed the edge of what the app can see, and the honest number to
   * report — some accounts will never be imported, so this is not expected to
   * reach zero.
   */
  unmatched: z.number().int(),
  /**
   * The signed net of those legs: what the period's money figures are out by if
   * every unmatched leg turns out to be real.
   */
  unmatchedPence: Pence,
});

/** Two rows a person says are one payment. Order does not matter. */
export const TransferPairInput = z.strictObject({ aId: Id, bId: Id });

export const TransferPairResult = z.object({ paired: z.boolean() });

export const TransactionBulkResult = z.object({
  updated: z.number().int(),
  /** Ids that matched nothing. Not an error: the list may have moved on. */
  missing: z.array(Id),
});

/**
 * Asking the model where a handful of uncategorised rows belong.
 *
 * The reply is a **proposal**, not a change: nothing is written by this
 * request, and the ids come back so the person can untick the ones they do not
 * want before the ordinary bulk edit applies the rest (docs/08 §8.9).
 */
export const SuggestionConfidence = z.enum(["high", "medium", "low"]);

/** A batch, not the ledger. Fifty is more than anyone reviews in one sitting. */
export const MAX_SUGGESTIONS = 50;

export const CategorySuggestionRequest = z.object({
  ids: z.array(Id).min(1).max(MAX_SUGGESTIONS),
});

export const CategorySuggestion = z.object({
  transactionId: Id,
  /** Resolved on the server: the model names a category, it never picks an id. */
  categoryId: Id,
  categoryName: z.string(),
  confidence: SuggestionConfidence,
  /** A few words on what in the description it went on. Shown beside the tick. */
  reason: z.string(),
});

export const CategorySuggestionList = z.object({
  data: z.array(CategorySuggestion),
  /**
   * How many of the ids were still uncategorised and therefore asked about.
   * Fewer suggestions than this is the model declining to guess, which is a
   * correct answer rather than a failure.
   */
  considered: z.number().int(),
});

/**
 * Emptying the ledger, to import it again from scratch.
 *
 * The household's own name has to be typed and is checked **on the server**,
 * not only in the dialog. A confirmation that lives entirely in the client is
 * a confirmation a mistyped `fetch` walks straight past, and this is the one
 * request in the app that cannot be undone.
 */
export const TransactionWipe = z.object({
  confirm: z.string().min(1).max(200),
});

export const TransactionWipeResult = z.object({
  deleted: z.number().int(),
  /** Uploads are kept, so "start again" means re-importing rather than rebuilding. */
  statementsKept: z.number().int(),
});

/**
 * Deleting a handful of rows the duplicates screen turned up, in one request.
 *
 * The cap is the same 500 the bulk edit uses. It is not a safety rail — the
 * safety is that a person ticked each row — but a bound on how much one
 * request can undo, and on how large a single audit entry gets.
 */
export const TransactionBulkDelete = z.object({
  ids: z.array(Id).min(1).max(500),
});

export const TransactionBulkDeleteResult = z.object({
  deleted: z.number().int(),
  /** Ids that matched nothing. Not an error: another tab may have got there first. */
  missing: z.array(Id),
});

/**
 * How sure the app is that a group is one payment recorded twice, rather than
 * two payments that happen to agree.
 *
 * `likely`  — the descriptions agree too, and the rows came from separate
 *             imports. This is what re-uploading a revised statement produces.
 * `possible`— separate imports, but the descriptions differ. A revised
 *             statement that also revised its wording looks like this, and so
 *             does a genuine second payment of the same amount.
 * `unlikely`— both rows arrived in the same file, so the bank itself reported
 *             the payment twice. Usually that means it really happened twice.
 */
export const DuplicateConfidence = z.enum(["likely", "possible", "unlikely"]);

export const DuplicateGroup = z.object({
  /** `accountId|date|amountPence`, which is what makes the group a group. */
  key: z.string(),
  accountId: Id,
  accountLabel: z.string(),
  date: IsoDate,
  amountPence: Pence,
  confidence: DuplicateConfidence,
  transactions: z.array(Transaction),
});

export const DuplicateQuery = z.object({
  month: IsoMonth.optional(),
  accountId: Id.optional(),
  /** Weaker groups are counted in `total` but left out of `data`. */
  minConfidence: DuplicateConfidence.default("possible"),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const DuplicateList = z.object({
  data: z.array(DuplicateGroup),
  /** Groups at or above `minConfidence`; `data` is capped at `limit`. */
  total: z.number().int(),
  /** Rows the scan gave up at, so a huge ledger says so rather than lying. */
  scanTruncated: z.boolean(),
});

export type TransactionSort = z.infer<typeof TransactionSort>;
export type SortDirection = z.infer<typeof SortDirection>;
export type TransactionFilter = z.infer<typeof TransactionFilter>;
export type BudgetProgress = z.infer<typeof BudgetProgress>;
export type TransactionList = z.infer<typeof TransactionList>;
export type MonthList = z.infer<typeof MonthList>;
export type TransactionPatch = z.infer<typeof TransactionPatch>;
export type TransactionBulkPatch = z.infer<typeof TransactionBulkPatch>;
export type TransactionBulkResult = z.infer<typeof TransactionBulkResult>;
export type TransferMatchQuery = z.infer<typeof TransferMatchQuery>;
export type TransferMatchResult = z.infer<typeof TransferMatchResult>;
export type TransferPairInput = z.infer<typeof TransferPairInput>;
export type TransferPairResult = z.infer<typeof TransferPairResult>;
export type SuggestionConfidence = z.infer<typeof SuggestionConfidence>;
export type CategorySuggestionRequest = z.infer<typeof CategorySuggestionRequest>;
export type CategorySuggestion = z.infer<typeof CategorySuggestion>;
export type CategorySuggestionList = z.infer<typeof CategorySuggestionList>;
export type TransactionBulkDelete = z.infer<typeof TransactionBulkDelete>;
export type TransactionBulkDeleteResult = z.infer<typeof TransactionBulkDeleteResult>;
export type TransactionWipe = z.infer<typeof TransactionWipe>;
export type TransactionWipeResult = z.infer<typeof TransactionWipeResult>;
export type DuplicateConfidence = z.infer<typeof DuplicateConfidence>;
export type DuplicateGroup = z.infer<typeof DuplicateGroup>;
export type DuplicateQuery = z.infer<typeof DuplicateQuery>;
export type DuplicateList = z.infer<typeof DuplicateList>;
